import {PlayerId} from '@glark-newco/game-library';
import type {IPointData} from '@pixi/core';
import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {Tile, TileId} from './signlinesBoardSlice';
import {dealRandom, dealTeamvineStrategy, PlayerHand} from '../utils/SignlinesDealUtils';


export type DealStrategies = 'Random' | 'Teamvine Strategy';

interface DealOption {
    name: DealStrategies,
    description: string,
    dealer: (hands: number) => PlayerHand[]
}

export const dealOptions: DealOption[] = [{
    name: 'Random',
    description: 'Tiles are assigned randomly between players.',
    dealer: (hands: number) => dealRandom(hands),
}, {
    name: 'Teamvine Strategy',
    description: 'Corner tiles are assigned to the smallest number of players. Edge tiles are next assigned to the smallest number of players. Center tiles are distributed amongst remaining players.',
    dealer: (hands: number) => dealTeamvineStrategy(hands),
}];

export interface SelectedPlayersTiles {
    [playerId: PlayerId]: TileId[] | [];
}

// ---------- Slice typings ----------
interface TileInteractionSlice {
    isDragging: boolean;
    tile?: Tile;
    isZoom: boolean;
    offset?: IPointData;
    position: IPointData;
    startDrag: (tile: Tile, offset: IPointData, cursor: IPointData) => void;
    updatePosition: (pos: IPointData) => void;
    endDrag: () => void;
    setZoom: (tile?: Tile) => void;
}

interface AvatarEditSlice {
    inEdit: boolean;
    setEdit: (edit: boolean) => void;
}

interface FacilitatorOptionsSlice {
    dealStrategy: DealStrategies;
    selectedPlayersTiles: Record<PlayerId, TileId[]>;
    setDealStrategy: (strategy: DealStrategies) => void;
    addSelectedPlayer: (ids: PlayerId | PlayerId[]) => void;
    removeSelectedPlayer: (id: PlayerId) => void;
    giveSelectedPlayersTiles: (map: Record<PlayerId, TileId[]>) => void;
}

// ---------- Store ----------
type UIStore = TileInteractionSlice &
AvatarEditSlice &
FacilitatorOptionsSlice & {
    reset: () => void;
};

export const useUIStore = create<UIStore>()(
    immer(set => ({
        // ---------- Tile interaction (drag + zoom) ----------
        isDragging: false,
        tile: undefined,
        isZoom: false,
        offset: undefined,
        position: {x: 960, y: 540}, // centre of the board (matches the old default)
        startDrag: (tile, offset, cursor) =>
            set(state => {
                state.isDragging = true;
                state.tile = tile;
                state.offset = offset;
                state.position = cursor;
                state.isZoom = false; // dragging always closes any zoom view
            }),
        updatePosition: pos =>
            set(state => {
                if (state.isDragging) state.position = pos;
            }),
        endDrag: () =>
            set(state => {
                state.isDragging = false;
                state.tile = undefined;
                state.offset = undefined;
                // keep `position` so the UI can still read where the drag ended (mirrors the original atom)
            }),
        setZoom: tile =>
            set(state => {
                state.isZoom = !!tile;
                state.tile = tile;          // reuse the same field
                state.isDragging = false;   // zoom view closes any drag
            }),

        // ---------- Avatar edit ----------
        inEdit: false,
        setEdit: (edit) => set(state => {state.inEdit = edit;}),

        // ---------- Facilitator options ----------
        dealStrategy: 'Random',
        selectedPlayersTiles: {},
        setDealStrategy: strategy => set(state => {state.dealStrategy = strategy;}),
        addSelectedPlayer: ids =>
            set(state => {
                const arr = Array.isArray(ids) ? ids : [ids];
                arr.forEach(id => {
                    if (!(id in state.selectedPlayersTiles)) {
                        state.selectedPlayersTiles[id] = [];
                    }
                });
            }),
        removeSelectedPlayer: id =>
            set(state => {
                delete state.selectedPlayersTiles[id];
            }),
        giveSelectedPlayersTiles: map => {
            // TODO - can we store or pass a simpler object than this map?
            const allTiles = Object.values(map).flat();
            if (new Set(allTiles).size !== allTiles.length) {
                console.error('Duplicate tiles detected');
                return;
            }
            set(state => {
                state.selectedPlayersTiles = {...map};
            });
        },

        // ---------- Global reset (used in test teardown) ----------
        reset: () =>
            set(state => {
                // Tile interaction
                state.isDragging = false;
                state.tile = undefined;
                state.isZoom = false;
                state.offset = undefined;
                state.position = {x: 960, y: 540};
                // Avatar edit
                state.inEdit = false;
                // Facilitator options
                state.dealStrategy = 'Random';
                state.selectedPlayersTiles = {};
            }),
    })),
);
